import { isFulfilled } from '@reduxjs/toolkit'
import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { ConfirmContextProvider } from 'legacy/lut/components/shared/Confirm/ConfirmContextProvider'
import { history } from 'legacy/lut/helpers/route'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { resetAllModifiedDataByKey } from 'cng-common/features/form'
import { deleteParamsFromUrl, getUrlWithParams } from 'core-components/helpers/url'
import { Loader } from 'core-components/library/loader'
import MeasureController from 'core/analytics/MeasureController'
import { ErrorMessage } from 'partner360/components/QBR/shared/errorMessage/ErrorMessage'
import { createDraftQbr } from 'partner360/features/QBR/api/createDraftQbr'
import { requestQbrDocument } from 'partner360/features/QBR/api/requestQbrDocument'
import { requestQbrDocumentPartners } from 'partner360/features/QBR/api/requestQbrDocumentPartners'

import { changeRTM, clearState, selectQbrDocumentError, setQbrEntryId } from '../../features/QBR'

import { QBR_SETTINGS_FORM_KEY } from './components/qbrDocumentDetails/components/steps/settingsStep/components/qbrSettingsForm'
import { QBRDocumentsDetail } from './components/qbrDocumentDetails/QBRDocumentsDetail'
import { QBRDocumentsList } from './components/qbrDocumentsList/QBRDocumentsList'
import {
  NEW_QBR_TEMP_ENTRY_ID,
  QBR_DOCUMENT_URL_IDENTIFIER,
  QBR_DOCUMENT_URL_PID,
  QBR_DOCUMENT_URL_RTM,
} from './configs'

interface IQBRProps {
  metrics: MeasureController
  widgetParams: IObjectAny
  entryId: string
  preDefinedRtm?: PartnerType
  preDefinedPid?: string
}

export const QBR = ({
  metrics,
  widgetParams,
  entryId,
  preDefinedRtm,
  preDefinedPid,
}: IQBRProps) => {
  const [isQbrLegacyDocumentMode, setIsQbrLegacyDocumentMode] = useState(false)
  const [isShowQBRDocumentDetail, setIsShowQBRDocumentDetail] = useState(false)
  const [documentEntryId, setDocumentEntryId] = useState<number | null>(
    entryId ? Number(entryId) : null
  )
  const [isLoading, setIsLoading] = useState(false)

  const qbrErrorMessage = useSelector(selectQbrDocumentError)

  const dispatch = useAppDispatch()

  const openDocumentDetail = useCallback(
    (documentId: number, isLegacyDocument: boolean) => {
      setIsShowQBRDocumentDetail(true)
      setIsQbrLegacyDocumentMode(isLegacyDocument)
      setDocumentEntryId(documentId)
      dispatch(setQbrEntryId(documentId))
      history.push(getUrlWithParams({ [QBR_DOCUMENT_URL_IDENTIFIER]: documentId }))
    },
    [dispatch]
  )

  const openQbrDocumentUploadDialog = useCallback(() => {
    setIsShowQBRDocumentDetail(true)
    setIsQbrLegacyDocumentMode(true)
  }, [])

  const showQbrDocumentDetail = useCallback(() => {
    setIsShowQBRDocumentDetail(true)
  }, [])

  const showListOfAllDocuments = useCallback(() => {
    setIsShowQBRDocumentDetail(false)
    setIsQbrLegacyDocumentMode(false)
    dispatch(clearState())
    dispatch(resetAllModifiedDataByKey(QBR_SETTINGS_FORM_KEY))
    setDocumentEntryId(null)
    history.push(
      deleteParamsFromUrl([QBR_DOCUMENT_URL_IDENTIFIER, QBR_DOCUMENT_URL_RTM, QBR_DOCUMENT_URL_PID])
    )
  }, [dispatch])

  const getQbrData = useCallback(
    async (documentId: number) => {
      setIsLoading(true)
      const requestQbrDocumentAction = await dispatch(requestQbrDocument({ documentId }))
      if (isFulfilled(requestQbrDocumentAction)) {
        const { document: qbrDocument } = requestQbrDocumentAction.payload

        if (qbrDocument) {
          setIsQbrLegacyDocumentMode(qbrDocument.IsLegacyDocument === 1)
          await dispatch(
            requestQbrDocumentPartners({
              entryId: documentId,
              partnerIds: qbrDocument.PartnerIDs,
              fYear: qbrDocument.FYear,
              settings: qbrDocument.Settings,
              rtm: qbrDocument.RTM,
            })
          )
        }
      }
      setIsLoading(false)
    },
    [dispatch]
  )

  useEffect(() => {
    if (documentEntryId === NEW_QBR_TEMP_ENTRY_ID && preDefinedRtm) {
      showQbrDocumentDetail()
      dispatch(changeRTM(preDefinedRtm))
      if (preDefinedPid) {
        dispatch(
          createDraftQbr({
            entryId: documentEntryId,
            partners: [preDefinedPid],
            rtm: preDefinedRtm,
            globalPartnerId: preDefinedPid.startsWith('GP') ? preDefinedPid : null,
          })
        )
      }
    } else if (documentEntryId) {
      setIsShowQBRDocumentDetail(true)
      getQbrData(documentEntryId)
    }
  }, [documentEntryId, getQbrData, preDefinedRtm, dispatch, preDefinedPid, showQbrDocumentDetail])

  if (isLoading) {
    return <Loader />
  }

  if (qbrErrorMessage) {
    return <ErrorMessage message={qbrErrorMessage} />
  }

  if (isShowQBRDocumentDetail) {
    return (
      <>
        <ConfirmContextProvider>
          <QBRDocumentsDetail
            isQbrLegacyDocumentMode={isQbrLegacyDocumentMode}
            widgetParams={widgetParams}
            metrics={metrics}
            showListOfAllDocuments={showListOfAllDocuments}
          />
        </ConfirmContextProvider>
      </>
    )
  }

  return (
    <>
      <QBRDocumentsList
        metrics={metrics}
        onCreateNewDocument={showQbrDocumentDetail}
        openDocumentDetail={openDocumentDetail}
        openQbrDocumentUploadDialog={openQbrDocumentUploadDialog}
      />
    </>
  )
}

export default QBR
